import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { showSuccess, showError, showWarning } from '../utils/notifications';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { logEdit, logLogout } from '../services/auditService';

/**
 * 🔒 POLÍTICA DE SEGURIDAD PARA EDICIÓN DE CA 🔒
 * 
 * CAMPOS QUE JAMÁS DEBEN SER EDITABLES POR EL USUARIO:
 * - updatedAt: Se asigna automáticamente con la fecha actual
 * - updatedBy: Se asigna automáticamente con el email del usuario
 * - createdAt: Nunca se modifica después de la creación
 * - email: Solo lectura (es la clave primaria)
 * - cedula: Solo lectura (dato crítico)
 * - balance: Solo lectura (se modifica solo por recargas)
 * 
 * ESTOS CAMPOS NO TIENEN INPUTS EN EL FORMULARIO Y SE MANEJAN
 * AUTOMÁTICAMENTE EN EL CÓDIGO PARA GARANTIZAR LA INTEGRIDAD
 */

function EditCA() {
  const { caId } = useParams();
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState('vendors');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Form fields
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [cedula, setCedula] = useState('');
  const [telefono, setTelefono] = useState('');
  const [direccion, setDireccion] = useState('');
  const [serial, setSerial] = useState('');
  const [zonaPrincipal, setZonaPrincipal] = useState('');
  const [subZona, setSubZona] = useState('');
  const [zona, setZona] = useState('No asignada');
  const [numberCa, setNumberCa] = useState('');
  const [status, setStatus] = useState(0);
  const [tipoVehiculos, setTipoVehiculos] = useState('autos_y_motos');

  // Data original para comparación
  const [originalData, setOriginalData] = useState({});

  // Estado para modal de eliminación
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCA, setDeletingCA] = useState(false);

  // Generar zonas principales (1-20)
  const zonasPrincipales = Array.from({length: 20}, (_, i) => ({
    value: i + 1,
    label: `Zona ${i + 1}`
  }));

  // Generar subzonas (01-100)
  const subZonas = Array.from({length: 100}, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: `${String(i + 1).padStart(2, '0')}`
  }));

  // Actualizar zona combinada cuando cambian zona principal o subzona
  useEffect(() => {
    if (zonaPrincipal && subZona) {
      setZona(`Z ${zonaPrincipal}-S${subZona}`);
    } else {
      setZona('No asignada');
    }
  }, [zonaPrincipal, subZona]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Verificar si el perfil del usuario existe en Firestore
          const userDocRef = doc(db, 'usuarios', currentUser.email);
          const userDoc = await getDoc(userDocRef);
          
          if (!userDoc.exists()) {
            showError("Usuario no encontrado", "Tu perfil no existe en el sistema");
            await auth.signOut();
            navigate('/login');
            return;
          }
          
          // El usuario existe, continuar normalmente
          setUser(currentUser);
          
          // Almacenar el rol del usuario
          const userData = userDoc.data();
          setUserRole(userData.rol || 'Usuario');
          
          // Verificar si tiene permisos de administrador, supervisor o recaudador
          if (userData.rol !== 'Admin' && userData.rol !== 'Supervisor' && userData.rol !== 'Recaudador') {
            showError("Acceso denegado", "No tienes permisos para editar Controladores Autorizados");
            navigate('/dashboard');
            return;
          }
        } catch (error) {
          console.error("Error al verificar perfil de usuario:", error);
          showError("Error", "Ocurrió un problema al verificar tu perfil");
          await auth.signOut();
          navigate('/login');
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Cargar datos del CA
  useEffect(() => {
    const loadCAData = async () => {
      if (!caId || !user) return;

      try {
        // Buscar el CA por email (caId es el email)
        const caDocRef = doc(db, 'usuarios', caId);
        const caDoc = await getDoc(caDocRef);

        if (!caDoc.exists()) {
          showError("CA no encontrado", "El Controlador Autorizado especificado no existe");
          navigate('/vendors');
          return;
        }

        const caData = caDoc.data();
        
        // Verificar que sea realmente un CA
        if (caData.rol !== 'CA') {
          showError("Error", "El usuario especificado no es un Controlador Autorizado");
          navigate('/vendors');
          return;
        }

        // Cargar datos en el formulario
        setNombre(caData.nombre || '');
        setEmail(caData.email || caId);
        setCedula(caData.cedula || '');
        setTelefono(caData.telefono || '');
        setDireccion(caData.direccion || '');
        setSerial(caData.serial || '');
        setNumberCa(caData.numberCa || '');
        setStatus(caData.status || 0);
        setZona(caData.zona || 'No asignada');
        setTipoVehiculos(caData.tipoVehiculos || 'autos_y_motos');

        // Parsear zona para rellenar zona principal y subzona
        if (caData.zona && caData.zona !== 'No asignada') {
          const zonaMatch = caData.zona.match(/Z (\d+)-S(\d+)/);
          if (zonaMatch) {
            setZonaPrincipal(zonaMatch[1]);
            setSubZona(zonaMatch[2]);
          }
        }

        // Guardar datos originales para comparación
        setOriginalData(caData);

      } catch (error) {
        console.error("Error al cargar datos del CA:", error);
        showError("Error", "No se pudieron cargar los datos del Controlador Autorizado");
        navigate('/vendors');
      }
    };

    loadCAData();
  }, [caId, user, navigate]);

  const handleDeleteCA = async () => {
    if (!caId) return;
    setDeletingCA(true);
    try {
      await deleteDoc(doc(db, 'usuarios', caId));
      showSuccess('CA Eliminado', `El Controlador Autorizado ${nombre} (${numberCa}) ha sido eliminado.`);
      navigate('/vendors');
    } catch (error) {
      showError('Error', `No se pudo eliminar el CA: ${error.message}`);
    } finally {
      setDeletingCA(false);
      setShowDeleteModal(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar campos obligatorios (campos visibles)
    if (!nombre || !email || !telefono || !direccion || !serial || !numberCa) {
      showWarning("Campos incompletos", "Todos los campos visibles son obligatorios");
      return;
    }
    
    // Validar formato de correo
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showWarning("Correo inválido", "Por favor ingresa un correo electrónico válido");
      return;
    }
    
    // La cédula no se puede editar, por lo que no necesita validación
    
    setSubmitting(true);
    
    try {
      // Verificar si han cambiado datos únicos (serial, numberCa)
      const usuariosRef = collection(db, 'usuarios');
      
      // Verificar serial si cambió
      if (serial !== originalData.serial) {
        const serialQuery = query(usuariosRef, where("serial", "==", serial));
        const serialSnapshot = await getDocs(serialQuery);
        if (!serialSnapshot.empty) {
          throw new Error(`El serial ${serial} ya está registrado en el sistema`);
        }
      }
      
      // Verificar numberCa si cambió
      if (numberCa !== originalData.numberCa) {
        const caQuery = query(usuariosRef, where("numberCa", "==", numberCa));
        const caSnapshot = await getDocs(caQuery);
        if (!caSnapshot.empty) {
          throw new Error(`El ID de CA "${numberCa}" ya está en uso`);
        }
      }

      // CRÍTICO: Preparar datos actualizados (solo campos editables y visibles)
      const updatedData = {
        // Campos editables y visibles por el usuario
        nombre,
        telefono,
        direccion,
        serial,
        numberCa,
        zona,
        status: parseInt(status),
        tipoVehiculos,
        
        // ⚠️ CAMPOS DE SEGURIDAD - NUNCA EDITABLES POR EL USUARIO ⚠️
        // 🔒 balance: NO se toca - campo oculto
        // 🔒 updatedAt: NO se toca - se mantiene el valor original
        // 🔒 updatedBy: NO se toca - se mantiene el valor original  
        // 🔒 cedula: NO se actualiza (readonly)
        // 🔒 email: NO se actualiza (readonly) 
        // 🔒 createdAt: NO se actualiza JAMÁS
      };

      // Actualizar en Firestore
      const caDocRef = doc(db, 'usuarios', caId);
      await updateDoc(caDocRef, updatedData);

      // Registrar la edición en logs de auditoría
      await logEdit(
        {email: user.email, rol: userRole},
        'usuarios',
        caId,
        originalData,
        updatedData
      );

      // Actualizar los datos originales con los nuevos valores para futuras ediciones
      setOriginalData({
        ...originalData,
        ...updatedData
      });

      showSuccess(
        "CA Actualizado Exitosamente",
        `Los datos del Controlador Autorizado ${nombre} (${numberCa}) han sido modificados correctamente.`
      );

      // Se mantiene en la misma página de edición - NO redirige

    } catch (error) {
      console.error("Error al actualizar CA:", error);
      showError("Error", `No se pudo actualizar el Controlador Autorizado: ${error.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* Mobile Header */}
      <MobileHeader 
        title="Editar CA" 
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* Add top spacing for mobile */}
      <div className="md:hidden h-14"></div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Overlay for mobile menu */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}
      
        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
        {/* Top Header */}
        <header className="bg-white shadow px-6 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-xl font-bold text-gray-900 ml-8 md:ml-0">
              Editar Controlador Autorizado - {numberCa}
            </h1>
            <div className="text-right flex items-center">
              <div className="mr-4">
                <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                <p className="text-xs text-gray-500">{userRole}</p>
              </div>
              <button
                onClick={async () => {
                  try {
                    await logLogout({email: user?.email, rol: userRole}, {
                      reason: 'manual',
                      userAgent: navigator.userAgent,
                      platform: navigator.platform,
                      timestamp: new Date().toISOString()
                    });
                    localStorage.removeItem('userRole');
                    await auth.signOut();
                    navigate('/login');
                  } catch (error) {
                    console.error("Error al cerrar sesión:", error);
                  }
                }}
                className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1 rounded"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-6">
              Modificar datos del Controlador Autorizado
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre completo</label>
                  <input
                    type="text"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: Carlos Pérez"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Correo electrónico</label>
                  <input
                    type="email"
                    value={email}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-600"
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    El correo no se puede modificar
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cédula</label>
                  <input
                    type="text"
                    value={cedula}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-600"
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    La cédula no se puede modificar
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                  <input
                    type="tel"
                    value={telefono}
                    onChange={(e) => setTelefono(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: 0991234567"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                  <input
                    type="text"
                    value={direccion}
                    onChange={(e) => setDireccion(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: Av. Principal 123, Centro"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serial del Dispositivo</label>
                  <input
                    type="text"
                    value={serial}
                    onChange={(e) => setSerial(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: SN123456789"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Número de serie único del dispositivo asignado
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ID de CA</label>
                  <input
                    type="text"
                    value={numberCa}
                    onChange={(e) => setNumberCa(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ej: CA01, CA59, etc."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(parseInt(e.target.value))}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={0}>Inactivo</option>
                    <option value={1}>Activo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Vehículos</label>
                  <select
                    value={tipoVehiculos}
                    onChange={(e) => setTipoVehiculos(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="autos_y_motos">🚗🏍️ Autos y Motos</option>
                    <option value="solo_autos">🚗 Solo Autos</option>
                    <option value="solo_motos">🏍️ Solo Motos</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Selecciona qué tipo de vehículos puede procesar este CA
                  </p>
                </div>

                {/* Saldo Actual - Campo oculto */}
              </div>
              
              {/* Sección de Zonas */}
              <div className="border-t pt-4 mt-6">
                <h3 className="text-md font-medium text-gray-800 mb-4">📍 Asignación de Zona</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Zona Principal</label>
                    <select
                      value={zonaPrincipal}
                      onChange={(e) => setZonaPrincipal(e.target.value)}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Selecciona zona principal</option>
                      {zonasPrincipales.map((zona) => (
                        <option key={zona.value} value={zona.value}>
                          {zona.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subzona</label>
                    <select
                      value={subZona}
                      onChange={(e) => setSubZona(e.target.value)}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={!zonaPrincipal}
                    >
                      <option value="">Selecciona subzona</option>
                      {subZonas.map((zona) => (
                        <option key={zona.value} value={zona.value}>
                          {zona.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Zona Final</label>
                    <input
                      type="text"
                      value={zona}
                      className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-600"
                      readOnly
                    />
                  </div>
                </div>
              </div>
              
              <div className="pt-4 flex items-center justify-between">
                {userRole === 'Admin' && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(true)}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Eliminar CA
                  </button>
                )}
                <div className="flex items-center space-x-3 ml-auto">
                  <button
                    type="button"
                    onClick={() => navigate('/vendors')}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {submitting ? 'Actualizando...' : 'Actualizar Controlador Autorizado'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
        </div>
      </div>

      {/* Modal confirmación eliminación */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Eliminar Controlador Autorizado</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              ¿Estás seguro de que deseas eliminar a <strong>{nombre} ({numberCa})</strong>? Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deletingCA}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteCA}
                disabled={deletingCA}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {deletingCA ? 'Eliminando...' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditCA;
